---
title: Perception
description: The DeepStream run, stage by stage, from decode to the Kafka topic.
---

Perception is one GStreamer graph built with NVIDIA DeepStream. Frames enter
as compressed video and leave as Kafka messages saying who was where, in
floor-plan meters, at every moment of the night. Frames stay in GPU memory
the whole way; nothing bounces through the CPU.

The stages below are directories in the ML service, numbered in graph order
(`s00` to `s60`). Each carries its own README on the machine; this page is
the same story in one place.

## Before the graph starts

The runner validates the session, generates the DeepStream config from
`store.yml`, runs the graph, and then verifies the output. Two details
matter:

- **Two watchdogs guard the run.** A hard wall-clock limit, and a stall
  detector that kills the run after ten minutes of no output. The stall
  watchdog exists because the muxer waits forever for a full batch, so a
  single dead decoder hangs the whole graph silently instead of crashing.
- **A clean exit is not proof of output.** Even on exit code zero the runner
  checks that the tracker actually wrote results. Success lines are cheap;
  artifacts are evidence.

Configuration generation also writes `manifest.json`: the handoff file that
analytics and review read later. It records the cameras, the frame rate,
which numeric sensor id maps to which camera name, and provenance: hashes of
the config and model that produced the night, so any disputed number can be
traced to its exact inputs.

## s00: sources and the session contract

Emits one source block per video and enforces the session contract before
anything runs. The muxer batches frame N of every source together
regardless of timestamps, so frame N must show the same real instant
everywhere. The tracker's own budgets say how tight that is: it searches one
frame either side when matching identities across cameras, and fuses peers
only within 1.35 meters. A walking person moves about 1.4 meters per
second, so keep camera starts within one or two frames.

What breaks here: unsynchronized recorder exports, missing calibration
files, a video whose name does not match its calibration file, or a
container format not in the accepted list, which makes the camera invisible
rather than wrong. All of these fail fast on purpose: an error here is
cheaper than silently garbage identities three stages later.

## s10: the stream muxer

Config-only. Batch size equals the camera count, and the muxer is told to
wait for the full batch forever rather than push a partial one, because a
partial batch would desynchronize the trackers' peer frame numbers. The
muxer's frame size is kept equal to the source resolution; it would scale
silently otherwise, and scaled pixels would invalidate calibration.

What breaks here: throughput stalls. If the frame rate drops to zero
mid-run, the muxer is starved: one source finished early or a decoder died.

## s20: the detector

RF-DETR finds people in each batched frame. The config is generated, and
the preprocessing numbers in it come from a verification script, never from
hand-editing: wrong values do not crash, they silently degrade every
detection. The model is NMS-free and outputs a fixed 300 candidates per
frame; a custom parser turns them into DeepStream objects.

What breaks here, and what each symptom means:

- **No detections at all**: wrong preprocessing values or a threshold set
  too high.
- **Boxes tilted at strange angles**: the parser lost its zero
  initialization.
- **Engine build fails at startup**: the exported model's batch size does
  not match the session's camera count; re-export.
- **Scores collapse in fp16**: the documented overflow in this model's
  normalization layers; run fp32, or validate fp16 first.

## s30: the tracker

The stage that makes or breaks every downstream number. Each camera runs
its own single-view 3D tracker, projecting people onto the floor plan
through that camera's calibration. The cameras then negotiate global
identities directly with each other over MQTT. There is no central fusion
service; the broker is internal plumbing.

By default every camera talks to every other camera. Stores with many
cameras can list only the neighbors whose views overlap; the list is
symmetrized automatically, because a one-way subscription silently halves
the handoff rate, and any camera left off the list stays connected to all,
so a partial map degrades safely instead of isolating a camera.

What breaks here:

- **Same person, different identity per camera**: peers cannot associate.
  Lower the match confidence slightly.
- **Two people share one identity**: raise it. If the problem persists,
  calibration is off.
- **World positions that are kilometers away**: a calibration file in the
  wrong units or coordinate frame.

Tune on rush-hour clips, never quiet ones: an empty store cannot show
identity mistakes.

## s40: the debug overlay

Off by default. When enabled it draws boxes and identity numbers on a tiled
view of all cameras, live in a window or recorded to a video artifact. It is
the artifact to reach for when numbers look wrong but logs look fine:
watching two minutes of overlay beats an hour of log reading. Identity
flicker points at s30, bad boxes point at s20, and the overlay shows which
camera a bad identity came from.

## s50: the message converter

Serializes every frame's tracked objects, identity, box and 3D floor
position, into a protobuf schema. That schema file is vendored in this
repository and is the Kafka contract: analytics decodes exactly what this
stage encodes, versioned together.

What breaks here: empty sensor ids in Kafka mean the sensor registry got
out of step with the source order, and missing 3D coordinates mean the
tracker was not asked to emit foot locations.

## s60: the Kafka sink

Publishes the protobuf frames to the night's topic. Kafka is the only
application bus in the system: analytics replays the topic from the
beginning on every run, which is why perception never re-runs just because
an analytics setting changed. Retention is seven days; a night older than
that must re-run perception before analytics can read it again.

What breaks here: if a peek at the topic shows nothing, check that the
broker is reachable from inside the container, that the Kafka client
library loaded cleanly in the logs, and that the topic name matches between
configuration and consumer.
